import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import BottomBlob from '../components/Blob/BottomBlob';
import ChaptersGrid from '../components/Comics/ChaptersGrid';
import ComicsFooter from '../components/Comics/ComicsFooter';
import ComicsHeader from '../components/Comics/ComicsHeader';
import RecentReadView from '../components/Comics/RecentReadView';
import {loadComics} from '../store/actions/comics';
import {
    selectComicByFriendlyName,
    selectComics,
    selectCurrentChapter,
    selectPendingChapters,
} from '../store/reducers';
import {mobileMediaQuery, tabletMediaQuery} from '../styles';

const ComicsDetails = () => {
    const dispatch = useDispatch();
    const loadedComics = useSelector(selectComics);
    const params = useParams();
    let comicId = params.comic_id;
    let comic = useSelector(state => selectComicByFriendlyName(state, comicId));
    let currentChapter = useSelector(
        state => selectCurrentChapter(state, comic) || comic?.chapters[0],
    );
    const continueReading = useSelector(selectPendingChapters).filter(
        chapter => {
            return comic?.chapters.findIndex(c => c.id === chapter.id) !== -1;
        },
    );

    useEffect(() => {
        if (!loadedComics) {
            dispatch(loadComics());
        }
    }, [dispatch, loadedComics]);
    const {t} = useTranslation();

    return (
        <Container>
            {comic && (
                <Centered>
                    <ComicsHeader comic={comic} />
                    <Separator />
                    {continueReading && continueReading.length > 0 && (
                        <Text>{t('comics.continueReading')}</Text>
                    )}
                    <RecentReadView
                        chapters={continueReading}
                        maxWidth={1088}
                        maxItems={2}
                        useChapterCover={true}
                    />
                    <Text>{t('comics.allChapters')}</Text>
                    <ChaptersGrid chapters={comic.chapters} />
                    <ComicsFooter comic={comic} />
                </Centered>
            )}
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    max-width: 1440px;
    align-self: center;
    display: flex;
`;

const Centered = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
`;

const Separator = styled.hr`
    height: 2px;
    width: 100%;

    border: none;
    margin-block: unset;
    background: linear-gradient(
        90deg,
        #ffd93f 0%,
        #00aeef 20%,
        #ffd93f 40%,
        #ffd93f 40%,
        #ec008c 80%,
        #ffd93f 100%
    );
`;

const Text = styled.div`
    font-family: 'TTNormsProExtraBlack';
    font-style: normal;
    font-size: 20px;
    line-height: 24px;
    text-transform: uppercase;
    color: white;
    width: calc(100% - 80px);
    max-width: 1008px;
    margin: 40px 0px 16px 0px;

    ${mobileMediaQuery} {
        width: calc(100% - 50px);
    }
`;

const Header = ({comic}) => {
    return (
        <HeaderContainer url={`${comic.baseUrl}${comic.characters}`}>
            <Logo src={`${comic.baseUrl}${comic.logo}`}></Logo>
            <BottomBlob />
        </HeaderContainer>
    );
};

const HeaderContainer = styled.div`
    display: flex;
    width: 100%;
    height: 467px;
    background: url(${props => props.url}) no-repeat right 0px;
    background-size: 75%;
    position: relative;

    ${tabletMediaQuery} {
        height: 317px;
        background-size: 860px 430px;
        background-position: calc(100% + 150px) 0px;
    }

    ${mobileMediaQuery} {
        height: 300px;
        background-position: center 0px;
        background-size: 616px 308px;
    }
`;

const Logo = styled.img`
    position: absolute;
    width: 373px;
    height: 373px;
    left: 80px;

    ${tabletMediaQuery} {
        width: 265px;
        height: 265px;
        left: 40px;
    }

    ${mobileMediaQuery} {
        width: 160px;
        height: 160px;
        bottom: 0px;
        left: calc(50% - 80px);
    }
`;

export default ComicsDetails;
